import { useEffect, useState } from "react";
import {
  Buildings,
  User,
  MapPin,
  Flag,
  CalendarBlank,
  PaintRoller,
  Scissors,
  Drop,
  CurrencyDollar,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const STAGE_STYLE = {
  Lead: "border-zinc-600 bg-zinc-800 text-zinc-200",
  Quoted: "border-blue-500/40 bg-blue-500/10 text-blue-200",
  Approved: "border-violet-500/40 bg-violet-500/10 text-violet-200",
  Template: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  Fabrication: "border-orange-500/40 bg-orange-500/10 text-orange-200",
  Install: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200",
  Complete: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  Lost: "border-red-500/40 bg-red-500/10 text-red-200",
};

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testId}>
    <Icon size={16} weight="bold" className="mt-0.5 shrink-0 text-zinc-500" />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm text-zinc-100">{children}</div>
    </div>
  </div>
);

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime())
    ? d
    : dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function CalendarEventDetail({ open, onOpenChange, event, onOpenRecord }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    setLoading(true);
    setData(null);
    api
      .get("/calendar/event-detail", {
        params: { entity: event.entity, id: event.id, kind: event.kind, date: event.date },
      })
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, event]);

  const d = data || {};
  const chips = (arr) =>
    (arr || []).map((v) => (
      <span
        key={v}
        className="inline-flex rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-xs text-zinc-100"
      >
        {v}
      </span>
    ));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calendar-event-detail" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {event?.kind && (
              <span className="inline-flex rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200">
                {event.kind}
              </span>
            )}
            <span className="truncate">{d.title || event?.title || event?.code || "Event"}</span>
          </DialogTitle>
          <DialogDescription>
            {d.code ? `${d.code} · ` : ""}Schedule entry details
          </DialogDescription>
        </DialogHeader>

        {!loading && (event?.entity === "job" || d.deal_total || d.invoiced || d.balance_due) ? (
          <div className="space-y-2" data-testid="detail-money">
            {d.pay_status ? (
              <span
                data-testid="detail-pay-status"
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                  d.pay_status === "Paid in full"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : d.pay_status === "Deposit paid"
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                      : "border-zinc-700 bg-zinc-800 text-zinc-300"
                }`}
              >
                {d.pay_status}
              </span>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <CurrencyDollar size={12} weight="bold" /> Deal total
              </div>
              <div className="mt-1 text-lg font-bold text-zinc-100" data-testid="detail-deal-total">
                {money(d.deal_total)}
              </div>
            </div>
            <div
              className={`rounded-lg border p-3 ${
                Number(d.balance_due) > 0.005
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-emerald-500/40 bg-emerald-500/10"
              }`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <CurrencyDollar size={12} weight="bold" /> Balance due
              </div>
              <div
                className={`mt-1 text-lg font-bold ${
                  Number(d.balance_due) > 0.005 ? "text-amber-200" : "text-emerald-200"
                }`}
                data-testid="detail-balance-due"
              >
                {money(d.balance_due)}
              </div>
            </div>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500" data-testid="calendar-detail-loading">
            Loading…
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            <Row icon={Buildings} label="Company" testId="detail-company">
              {d.company || "—"}
            </Row>
            <Row icon={User} label="Customer" testId="detail-customer">
              {d.customer_contact || d.customer_name || "—"}
            </Row>
            <Row icon={MapPin} label="Job address" testId="detail-address">
              {d.job_address || "—"}
            </Row>
            <Row icon={Flag} label="Project stage" testId="detail-stage">
              {d.stage ? (
                <span
                  className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${
                    STAGE_STYLE[d.stage] || "border-zinc-600 bg-zinc-800 text-zinc-200"
                  }`}
                >
                  {d.stage}
                </span>
              ) : (
                "—"
              )}
            </Row>
            <Row icon={CalendarBlank} label="Workflow date" testId="detail-workflow-date">
              {fmtDate(d.workflow_date)}
              {(d.time || (d.window && d.window !== "All day")) && (
                <span className="ml-2 text-xs text-zinc-400">
                  {[d.time, d.window && d.window !== "All day" ? d.window : ""].filter(Boolean).join(" · ")}
                </span>
              )}
              {d.crew && <span className="ml-2 text-xs text-zinc-500">· {d.crew}</span>}
            </Row>
            <Row icon={PaintRoller} label="Slab color quoted" testId="detail-colors">
              {d.colors?.length ? <span className="flex flex-wrap gap-1.5">{chips(d.colors)}</span> : "—"}
            </Row>
            <Row icon={Scissors} label="Edge quoted" testId="detail-edges">
              {d.edges?.length ? <span className="flex flex-wrap gap-1.5">{chips(d.edges)}</span> : "—"}
            </Row>
            <Row icon={Drop} label="Sink information" testId="detail-sinks">
              {d.sinks?.length ? (
                <ul className="space-y-0.5">
                  {d.sinks.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              ) : (
                "—"
              )}
            </Row>
            {d.notes && (
              <Row icon={Flag} label="Notes" testId="detail-notes">
                {d.notes}
              </Row>
            )}
          </div>
        )}

        <DialogFooter>
          {event?.entity !== "custom" && (
            <Button
              data-testid="calendar-detail-open"
              onClick={() => onOpenRecord?.()}
              className="bg-indigo-500 text-white hover:bg-indigo-400"
            >
              <ArrowSquareOut size={15} weight="bold" className="mr-1.5" /> Open record
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
